import React from 'react';
import Buttons from '../Common/Buttons';
import Ticks from '../Common/Ticks';
import Trial from '../Home/Trial';
import Footer from '../Footer/Footer';
import Benefits from '../Home/Benefits';
import { useLocale } from '../../localization/LocaleContext';
import './NicheLandingPage.css';

const featureIcons = [
    // Invoice / document icon
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
    </svg>,
    // Send / share icon
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="22" y1="2" x2="11" y2="13"/>
        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>,
    // Chart / tracking icon
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="20" x2="12" y2="10"/>
        <line x1="18" y1="20" x2="18" y2="4"/>
        <line x1="6" y1="20" x2="6" y2="16"/>
    </svg>,
];

function InvoiceMockup({ t, nicheKey }) {
    return (
        <div className="niche-invoice-mockup">
            <div className="niche-invoice-header">
                <div className="niche-invoice-logo-placeholder">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 21h18"/>
                        <path d="M5 21V7l7-4 7 4v14"/>
                        <path d="M9 21v-6h6v6"/>
                        <path d="M9 9h1"/>
                        <path d="M14 9h1"/>
                        <path d="M9 13h1"/>
                        <path d="M14 13h1"/>
                    </svg>
                </div>
                <div>
                    <div className="niche-invoice-title-text">INVOICE</div>
                    <div className="niche-invoice-number">#INV-2026-0042</div>
                </div>
            </div>
            <div className="niche-invoice-divider" />
            <div className="niche-invoice-parties">
                <div>
                    <div className="niche-invoice-label">{t(`niche.${nicheKey}.invoiceFrom`)}</div>
                    <div className="niche-invoice-name">{t(`niche.${nicheKey}.invoiceSender`)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div className="niche-invoice-label">{t(`niche.${nicheKey}.invoiceTo`)}</div>
                    <div className="niche-invoice-name">{t(`niche.${nicheKey}.invoiceRecipient`)}</div>
                </div>
            </div>
            <div className="niche-invoice-table">
                <div className="niche-invoice-table-header">
                    <span>{t(`niche.${nicheKey}.invoiceItemHeader`)}</span>
                    <span>{t(`niche.${nicheKey}.invoiceAmountHeader`)}</span>
                </div>
                <div className="niche-invoice-table-row">
                    <span>{t(`niche.${nicheKey}.invoiceItem1`)}</span>
                    <span>{t(`niche.${nicheKey}.invoiceItem1Price`)}</span>
                </div>
                <div className="niche-invoice-table-row">
                    <span>{t(`niche.${nicheKey}.invoiceItem2`)}</span>
                    <span>{t(`niche.${nicheKey}.invoiceItem2Price`)}</span>
                </div>
            </div>
            <div className="niche-invoice-total-row">
                <span className="niche-invoice-total-label">Total</span>
                <span className="niche-invoice-total-value">{t(`niche.${nicheKey}.invoiceTotal`)}</span>
            </div>
            <div className="niche-invoice-status">{t(`niche.${nicheKey}.invoiceStatus`)}</div>
        </div>
    );
}

export default function NicheLandingPage({ nicheKey }) {
    const { t } = useLocale();

    const features = [1, 2, 3].map((num, idx) => ({
        id: num,
        icon: featureIcons[idx],
        title: t(`niche.${nicheKey}.feature${num}Title`),
        description: t(`niche.${nicheKey}.feature${num}Description`),
    }));

    return (
        <>
            {/* Hero */}
            <section className="niche-hero sp-60">
                <div className="container">
                    <div className="row flex-column-reverse flex-lg-row align-items-center">
                        <div className="col-lg-6">
                            <h1 className="h-1 h-bold niche-hero-title">
                                {t(`niche.${nicheKey}.heroTitle`)}{' '}
                                <span className="niche-hero-highlight">
                                    {t(`niche.${nicheKey}.heroHighlight`)}
                                </span>
                            </h1>
                            <p className="niche-hero-description">
                                {t(`niche.${nicheKey}.heroDescription`)}
                            </p>
                            <Buttons />
                            <div className="mt-20">
                                <Ticks />
                            </div>
                        </div>
                        <div className="col-lg-6 mb-4 mb-lg-0">
                            <InvoiceMockup t={t} nicheKey={nicheKey} />
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="niche-features sp-90">
                <div className="container">
                    <div className="row">
                        {features.map(f => (
                            <div className="col-lg-4" key={f.id}>
                                <div className="niche-feature-card">
                                    <div className="niche-feature-icon">{f.icon}</div>
                                    <h3 className="niche-feature-title">{f.title}</h3>
                                    <p className="niche-feature-desc">{f.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="niche-cta-section sp-90">
                <div className="container">
                    <h2 className="h-1 h-bold niche-cta-title">
                        {t(`niche.${nicheKey}.ctaTitle`)}
                    </h2>
                    <p className="niche-cta-description">
                        {t(`niche.${nicheKey}.ctaDescription`)}
                    </p>
                    <Buttons />
                </div>
            </section>

            <Benefits />
            <Trial />
            <Footer />
        </>
    );
}
